import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  DataTable,
  Page,
  Card,
  Text,
  ButtonGroup,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  InlineStack,
  Box,
  Spinner,
  FormLayout,
  Image,
  Link,
  Divider,
  Tag,
  Toast,
  TextContainer,
  Icon,
} from "@shopify/polaris";
import axios from "../../../Assets/Lib/axios";
import emptyListImage from "../../../Assets/Images/emptyList.svg";
import MyPagination from "@material-ui/lab/Pagination";
import { useParams, useNavigate } from "react-router-dom";
import { EditIcon, DeleteIcon } from "@shopify/polaris-icons"; // added DeleteIcon
import countries from "../../../Data/countries.json";
import lebanonIcon from "../../../Assets/Images/lb.svg";
import iraqIcon from "../../../Assets/Images/iq.svg";
import egyptIcon from "../../../Assets/Images/eg.svg";

// ... keep all imports the same
import { SaveMinor } from "@shopify/polaris-icons"; // for save icon

const BenchmarkSubcategories = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  // Filters state
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("All");
  const [country, setCountry] = useState("Lebanon");
  const countryButtons = [
    { label: "All", value: "All" },
    { label: "Lebanon", value: "Lebanon", icon: lebanonIcon },
    { label: "Egypt", value: "Egypt", icon: egyptIcon },
    { label: "Iraq", value: "Iraq", icon: iraqIcon },
  ];

  // Data state
  const [items, setItems] = useState(null);
  const [result, setResult] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isListEmpty, setIsListEmpty] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 50;
  const refBoolPage = useRef(true);

  // Toast
  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive(!active), []);
  const [toastContent, setToastContent] = useState("");

  // Modal for Add/Edit
  const [modalActive, setModalActive] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [currentName, setCurrentName] = useState("");
  const [currentEditId, setCurrentEditId] = useState(null);

  // Modal for Delete
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [currentDeleteId, setCurrentDeleteId] = useState(null);

  const handleModalToggle = useCallback(
    () => setModalActive(!modalActive),
    [modalActive]
  );

  const handleDeleteModalToggle = useCallback(
    () => setDeleteModalActive(!deleteModalActive),
    [deleteModalActive]
  );

  const handleAddItem = () => {
    setModalMode("add");
    setCurrentName("");
    setModalActive(true);
  };

  const handleEdit = (id, name) => {
    setModalMode("edit");
    setCurrentName(name || "");
    setCurrentEditId(id);
    setModalActive(true);
  };

  const handleDelete = (id) => {
    setCurrentDeleteId(id);
    setDeleteModalActive(true);
  };

  const handleSaveName = async () => {
    setIsSaving(true);
    const form_data = new FormData();
    form_data.append("name", currentName);
    try {
      if (modalMode === "add") {
        // Call API to add
        await axios.post(
          `/benchmarks/categories/${id}/subcategories`,
          form_data
        );
        setToastContent("Category added successfully");
      } else {
        // Call API to edit
        form_data.append("_method", "PATCH");
        await axios.post(
          `/benchmarks/categories/${id}/subcategories/${currentEditId}`,
          form_data
        );
        setToastContent("Category updated successfully");
      }
      toggleActive();
      fetchData(); // refresh list
      handleModalToggle();
    } catch (err) {
      console.error(err);
    }
    setIsSaving(false);
  };

  const confirmDelete = async () => {
    setIsSaving(true);
    try {
      await axios.delete(
        `/benchmarks/categories/${id}/subcategories/${currentDeleteId}`
      );
      setToastContent("Subcategory deleted successfully");
      toggleActive();
      fetchData();
    } catch (err) {
      console.error(err);
    }
    setDeleteModalActive(false);
    setIsSaving(false);
  };

  const handleRowClick = (id) => {
    navigate(`/admin/Subcategories/${id}/metrics`);
  };

  const handleChangePage = (event, value) => {
    refBoolPage.current = true;
    setPage(value);
  };

  // Fetch data function
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("per_page", perPage);
      params.append("page", refBoolPage.current ? page : 1);
      if (search) params.append("filter[name]", search);

      const response = await axios.get(
        `/benchmarks/categories/${id}/subcategories`,
        { params }
      );
      const data = response.data.data.data;

      setResult(data);
      setItems(
        data.map((item) => [
          <div onClick={() => handleRowClick(item.id)} className="row_click">
            {item.name || "-"}
          </div>,

          <div className="me-3">
            <ButtonGroup>
              <Button
                onClick={() => handleEdit(item.id, item.name)}
                icon={EditIcon}
              >
                Edit
              </Button>
              <Button
                onClick={() => handleDelete(item.id)}
                icon={DeleteIcon}
                tone="critical"
              >
                Delete
              </Button>
            </ButtonGroup>
          </div>,
        ])
      );

      setTotalPages(response.data.data.total);
      setIsListEmpty(!data.length);
    } catch (err) {
      console.error(err);
      setIsListEmpty(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [page, search, channelFilter, country]);

  return items ? (
    <Page
      title="Subcategories"
      primaryAction={
        <Button variant="primary" onClick={handleAddItem}>
          Add Subcategory
        </Button>
      }
      backAction={{
        onAction: () => navigate(`/admin/benchmarks/edit/categories`),
      }}
    >
      {/* DataTable */}
      {items?.length ? (
        <Card padding="0">
          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            headings={["Name", ""]}
            rows={items}
            verticalAlign="middle"
            stickyHeader
            footerContent={
              items?.length &&
              `Showing ${items.length} of ${totalPages} results`
            }
          />
        </Card>
      ) : (
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "middle",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ width: "25%" }}>
              <img src={emptyListImage}></img>
            </div>
            <div className="mb-3">
              <Text variant="headingLg" as="h5" color="subdued">
                No results found
              </Text>
            </div>
            <br />
          </div>
        </Card>
      )}

      {/* Pagination */}
      {Math.ceil(totalPages / perPage) >= 2 && (
        <MyPagination
          count={Math.ceil(totalPages / perPage)}
          page={page}
          onChange={handleChangePage}
          size="large"
        />
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={modalActive}
        onClose={handleModalToggle}
        title={modalMode === "add" ? "Add Subctegory" : "Edit Subcategory"}
        primaryAction={{
          content: "Save",
          onAction: handleSaveName,
        }}
      >
        <Modal.Section>
          <TextField
            label="Subcategory Name"
            value={currentName}
            onChange={setCurrentName}
            autoComplete="off"
            placeholder="Enter name"
          />
        </Modal.Section>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModalActive}
        onClose={handleDeleteModalToggle}
        title="Delete Subcategory"
        primaryAction={{
          content: "Yes",
          destructive: true,
          onAction: confirmDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            loading: isSaving,
            onAction: handleDeleteModalToggle,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this subcategory?</Text>
        </Modal.Section>
      </Modal>

      {active && <Toast content={toastContent} onDismiss={toggleActive} />}
    </Page>
  ) : (
    <div className="spinnerContainer">
      <div className="vertical-center">
        <Spinner size="large" />
      </div>
    </div>
  );
};

export default BenchmarkSubcategories;
